import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  IconButton,
  Skeleton,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SpeedIcon from "@mui/icons-material/Speed";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import LeaderboardOutlinedIcon from "@mui/icons-material/LeaderboardOutlined";
import DonutSmallOutlinedIcon from "@mui/icons-material/DonutSmallOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import plusIcon from "../../../assets/plusicon.png";
import minusIcon from "../../../assets/minusIcon.png";
import {
  toFinancialImpactPayload,
  toInsightsPayload,
  toSpecialityAuditPayload,
} from "../WorkflowDashBoard/utils/mapDashboardFilters";

import {
  BORDER,
  TEXT_MUTED,
  TEXT_HEADING,
  BRAND_BLUE,
  CARD_SHADOW,
  CHART_LABELS_10PT,
  formatYTick,
  niceCeil,
  formatRelativeTime,
} from "./designTokens";
import { BreakdownRouter } from "./Breakdown";
import {
  fetchCodeChanges,
  fetchCoderAccuracy,
  fetchFinancialImpact,
  fetchSpecialtyAuditOutcomes,
  fetchTopDrg,
} from "./redux/WorkflowDashboardSlicer";
import { useDispatch, useSelector } from "react-redux";
import { useRelativeTimeTick } from "./utils/useRelativeTimeTick";

const BADGE_STYLES = {
  Good: { bg: "#DCFCE7", color: "#15803D" },
  Medium: { bg: "#FEF3C7", color: "#B45309" },
  Bad: { bg: "#FEE2E2", color: "#B91C1C" },
};
// place above the component, after imports
const INSIGHT_FETCH_MAP = {
  "coder-accuracy": (filters) => fetchCoderAccuracy(toInsightsPayload(filters)),
  "diagnosis-changes": (filters) => fetchCodeChanges(toInsightsPayload(filters)),
  "top-drg-changes": (filters) => fetchTopDrg(toInsightsPayload(filters)),
  "specialty-audit-outcomes": (filters) => fetchSpecialtyAuditOutcomes(toSpecialityAuditPayload(filters)),
  "confirmed-financial-impact": (filters) => fetchFinancialImpact(toFinancialImpactPayload(filters)),
};

// Fetches only the insight ids passed in — used by the Insights tab (all ids)
// and by the My Dashboard pinned section (only pinned ids).
export function fetchInsightsByIds(dispatch, filters, ids) {
  ids.forEach((id) => {
    const makeThunk = INSIGHT_FETCH_MAP[id];
    if (makeThunk) dispatch(makeThunk(filters));
  });
}

function SpecialtyAuditTile({ insight }) {
  const { completionRate, topSpecialty, statusBreakdown } = insight;

  const total =
    statusBreakdown.noChange +
    statusBreakdown.recoded +
    statusBreakdown.cdqSent;

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          gap: { xs: 1.5, sm: 0 },
          mt: 0.5,
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
            COMPLETION RATE
          </Typography>
          <Typography sx={{ fontSize: { xs: 24, sm: 28 }, fontWeight: 700 }}>
            {completionRate}%
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
            TOP AUDITED SPECIALTY
          </Typography>
          <Typography fontWeight={700}>{topSpecialty.name}</Typography>
          <Typography sx={{ fontSize: 12, color: "#94A3B8" }}>
            {topSpecialty.episodes.toLocaleString()} Most Audited Episodes
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 10.5,
          color: "#64748B",
          fontWeight: 700,
          mt: 2,
          mb: 0.75,
        }}
      >
        EPISODE STATUS BREAKDOWN
      </Typography>

      <Box
        sx={{
          display: "flex",
          height: 8,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${(statusBreakdown.noChange / total) * 100}%`,
            bgcolor: "#BFDBFE",
          }}
        />
        <Box
          sx={{
            width: `${(statusBreakdown.recoded / total) * 100}%`,
            bgcolor: "#E2E8F0",
          }}
        />
        <Box
          sx={{
            width: `${(statusBreakdown.cdqSent / total) * 100}%`,
            bgcolor: "#EF4444",
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
        {[
          ["No Change", statusBreakdown.noChange, "#BFDBFE"],
          ["Recoded", statusBreakdown.recoded, "#F3F4F6"],
          ["CDQ Sent", statusBreakdown.cdqSent, "#EF4444"],
        ].map(([label, val, color]) => (
          <Box
            key={label}
            sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
          >
            <Box
              sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }}
            />
            <Typography fontSize={12}>
              {label} ({val})
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
}

function TopDrgTile({ insight }) {
  const max = Math.max(...insight.drgs.map((d) => d.count));

  return (
    <>
      <Box sx={{ mt: 1.5 }}>
        {insight.drgs.map((drg) => (
          <Box
            key={drg.rank}
            sx={{
              display: "grid",
              gridTemplateColumns: "16px 1fr auto auto",
              alignItems: "center",
              columnGap: 1,
              mb: 1,
            }}
          >
            <Typography
              sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}
            >
              {drg.rank}
            </Typography>

            <Box
              sx={{
                height: 8,
                bgcolor: "#E2E8F0",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${(drg.count / max) * 100}%`,
                  height: "100%",
                  bgcolor: BRAND_BLUE,
                  borderRadius: 10,
                }}
              />
            </Box>

            <Typography
              sx={{
                width: { xs: 64, sm: 80 },
                fontSize: 11,
                color: "#475569",
                ml: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {drg.name}
            </Typography>

            <Typography
              sx={{
                width: 38,
                textAlign: "right",
                fontSize: 11,
                fontWeight: 700,
                color: "#334155",
              }}
            >
              +{drg.count}
            </Typography>
          </Box>
        ))}
      </Box>
      {/* 
      <Button
        sx={{
          mt: 1,
          px: 0,
          minWidth: 0,
          textTransform: "none",
          fontSize: 12,
          fontWeight: 600,
          color: BRAND_BLUE,
          justifyContent: "flex-start",
        }}
      >
        View all 10 DRGs
      </Button> */}
    </>
  );
}
function AccuracyRateTile({ insight }) {
  const { value, correct, incorrect, reviewedEpisodes } = insight;
  const total = correct + incorrect;
  const correctPct = (correct / total) * 100;

  return (
    <>
      <Typography
        sx={{
          fontSize: { xs: 24, sm: 28 },
          fontWeight: 700,
          color: "#0F172A",
          mt: 0.5,
        }}
      >
        {value}
      </Typography>

      <Box
        sx={{
          display: "flex",
          height: 8,
          mt: 2,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <Box sx={{ width: `${correctPct}%`, bgcolor: BRAND_BLUE }} />
        <Box sx={{ width: `${100 - correctPct}%`, bgcolor: "#EF4444" }} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: BRAND_BLUE,
            }}
          />
          <Typography sx={{ fontSize: 12, color: TEXT_HEADING }}>
            Correct: {correct}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: "#EF4444",
            }}
          />
          <Typography sx={{ fontSize: 12, color: TEXT_HEADING }}>
            Incorrect: {incorrect}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
          REVIEWED EPISODES
        </Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, color: TEXT_HEADING }}>
          {reviewedEpisodes}
        </Typography>
      </Box>
    </>
  );
}

function CodeChangesTile({ insight }) {
  const { value, categories } = insight;

  return (
    <>
      <Typography
        sx={{
          fontSize: { xs: 24, sm: 28 },
          fontWeight: 700,
          color: "#0F172A",
          mt: 0.5,
        }}
      >
        {value}
      </Typography>

      <Box sx={{ mt: 2 }}>
        {categories.map((cat) => (
          <Box key={cat.label} sx={{ mb: 1.5 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography sx={{ fontSize: 12, color: TEXT_HEADING }}>
                {cat.label}
              </Typography>
              <Typography
                sx={{ fontSize: 12, fontWeight: 700, color: TEXT_HEADING }}
              >
                {cat.value.toLocaleString()} · {cat.percent}%
              </Typography>
            </Box>
            <Box
              sx={{
                height: 6,
                bgcolor: "#EEF2F6",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  width: `${cat.percent}%`,
                  height: "100%",
                  bgcolor: cat.color,
                  borderRadius: 10,
                }}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
}

function FinancialImpactTile({ insight }) {
  const { value, totalConfirmedRevenue,totalConfirmedEpisodes, avgRevenuePerEpisode } = insight;
  console.log("FinancialImpactTile", insight, totalConfirmedRevenue, totalConfirmedEpisodes, avgRevenuePerEpisode);

  return (
    <>
      <Typography
        sx={{
          fontSize: { xs: 24, sm: 28 },
          fontWeight: 700,
          color: "#0F172A",
          mt: 0.5,
        }}
      >
        {value}
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
          TOTAL CONFIRMED EPISODES
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT_HEADING }}>
          {totalConfirmedEpisodes?.toLocaleString()}
        </Typography>
      </Box>

      <Box sx={{ mt: 1.5 }}>
        <Typography sx={{ fontSize: 11, color: "#64748B", fontWeight: 700 }}>
          AVG REVENUE PER EPISODE
        </Typography>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: TEXT_HEADING }}>
          ${avgRevenuePerEpisode.toLocaleString()}
        </Typography>
      </Box>
    </>
  );
}

function useMeasuredSize() {
  const containerRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect || {};
      if (width && height) setSize({ width, height });
    });
    ro.observe(el);
    setSize({ width: el.clientWidth, height: el.clientHeight });

    return () => ro.disconnect();
  }, []);

  return [containerRef, size];
}

function AxisLineChart({
  points = [],
  labels = [],
  color = "#1565C0",
  yTickCount = 4,
  formatY = formatYTick,
  isCurrency = true,
}) {
  const [containerRef, { width, height }] = useMeasuredSize();

  if (points.length === 0) return null;
  if (width === 0 || height === 0) {
    return (
      <Box ref={containerRef} sx={{ width: "100%", height: "100%", flex: 1 }} />
    );
  }

  const PADDING_LEFT = 40;
  const PADDING_BOTTOM = 22;
  const PADDING_TOP = 10;
  const PADDING_RIGHT = 24;

  const plotWidth = width - PADDING_LEFT - PADDING_RIGHT;
  const plotHeight = height - PADDING_TOP - PADDING_BOTTOM;

  const max = niceCeil(Math.max(...points));
  const min = 0;
  const range = max - min || 1;

  const xFor = (i) => PADDING_LEFT + (i / (points.length - 1)) * plotWidth;
  const yFor = (v) =>
    PADDING_TOP + plotHeight - ((v - min) / range) * plotHeight;

  const linePath = points
    .map(
      (p, i) =>
        `${i === 0 ? "M" : "L"}${xFor(i).toFixed(1)},${yFor(p).toFixed(1)}`,
    )
    .join(" ");

  const yTicks = Array.from(
    { length: yTickCount + 1 },
    (_, i) => (max / yTickCount) * i,
  ).reverse();

  const xLabelIndices = (() => {
    if (labels.length === 0) return [];
    const count = Math.min(5, labels.length);
    if (count === 1) return [0];
    const step = (labels.length - 1) / (count - 1);
    return Array.from({ length: count }, (_, i) => Math.round(i * step));
  })();

  return (
    <Box
      ref={containerRef}
      sx={{ width: "100%", height: "100%", flex: 1, overflow: "hidden" }}
    >
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {yTicks.map((tick, i) => {
          const y = yFor(tick);
          return (
            <g key={i}>
              <line
                x1={PADDING_LEFT}
                x2={width - PADDING_RIGHT}
                y1={y}
                y2={y}
                stroke="#EEF2F6"
                strokeWidth={1}
              />
              <text
                x={PADDING_LEFT - 8}
                y={y + 3}
                textAnchor="end"
                fontSize={9}
                fill="#94A3B8"
              >
                {isCurrency ? formatY(tick) : Math.round(tick)}
              </text>
            </g>
          );
        })}

        <line
          x1={PADDING_LEFT}
          x2={width - PADDING_RIGHT}
          y1={height - PADDING_BOTTOM}
          y2={height - PADDING_BOTTOM}
          stroke="#E2E8F0"
          strokeWidth={1}
        />

        {xLabelIndices.map((idx, i) => (
          <text
            key={idx}
            x={xFor(idx)}
            y={height - 6}
            textAnchor={
              i === 0
                ? "start"
                : i === xLabelIndices.length - 1
                  ? "end"
                  : "middle"
            }
            fontSize={9}
            fill="#94A3B8"
          >
            {labels[idx]}
          </text>
        ))}

        <path
          d={linePath}
          fill="none"
          stroke={color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {points.map((p, i) => (
          <circle
            key={i}
            cx={xFor(i)}
            cy={yFor(p)}
            r={i === points.length - 1 ? 4 : 3}
            fill={color}
            stroke="#fff"
            strokeWidth={1.5}
          />
        ))}
      </svg>
    </Box>
  );
}

function KPITile({ insight }) {
  return (
    <>
      <Typography
        sx={{
          fontSize: { xs: 24, sm: 28 },
          fontWeight: 700,
          color: "#0F172A",
          lineHeight: 1.15,
          mt: 0.5,
        }}
      >
        {insight.value}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          mt: 0.25,
          fontSize: 12,
          fontWeight: 500,
          color: insight.trendDirection === "up" ? "#16A34A" : "#DC2626",
        }}
      >
        <span>{insight.trendDirection === "up" ? "▲" : "▼"}</span>
        <span>{insight.trendLabel}</span>
      </Box>

      {Array.isArray(insight.chartData) && insight.chartData.length > 0 && (
        <Box
          sx={{
            mt: 1,
            mb: 1,
            flex: 1,
            minHeight: { xs: 90, sm: 100 },
            display: "flex",
          }}
        >
          <AxisLineChart
            points={insight.chartData}
            labels={insight.chartLabels || []}
            color={insight.chartColor}
            isCurrency={insight.chartIsCurrency !== false}
          />
        </Box>
      )}
    </>
  );
}

function InsightTileSkeleton() {
  return (
    <Paper
      elevation={0}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        p: { xs: 1.5, sm: 2 },
        border: BORDER,
        borderRadius: 2.5,
        boxShadow: CARD_SHADOW,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Skeleton
          variant="rounded"
          width={32}
          height={32}
          sx={{ borderRadius: 1.5 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton variant="text" width="60%" height={18} />
          <Skeleton variant="text" width="85%" height={16} />
        </Box>
        <Skeleton
          variant="rounded"
          width={48}
          height={20}
          sx={{ borderRadius: 10 }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          minHeight: 0,
          mt: 0.5,
        }}
      >
        <Skeleton variant="text" width="40%" height={32} />
        <Skeleton
          variant="rounded"
          width="100%"
          height={8}
          sx={{ borderRadius: 10 }}
        />
        <Skeleton variant="text" width="70%" height={16} />
        <Skeleton variant="text" width="55%" height={16} />
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #EEF2F6",
          pt: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: "auto",
        }}
      >
        <Skeleton variant="text" width={110} height={14} />
        <Box sx={{ display: "flex", gap: 1 }}>
          <Skeleton
            variant="rounded"
            width={110}
            height={28}
            sx={{ borderRadius: 2 }}
          />
          <Skeleton
            variant="rounded"
            width={32}
            height={32}
            sx={{ borderRadius: 1.5 }}
          />
        </Box>
      </Box>
    </Paper>
  );
}
// ---------------------------------------------------------------------------
// InsightTile — one summary card
// ---------------------------------------------------------------------------
function InsightTile({
  insight,
  pinned,
  onTogglePin,
  onOpenBreakdown,
  iconVariant = "plus", // "plus" (Insights tab) | "minus" (dashboard pinned tile)
  disableOpenBreakdown = false, // true on the dashboard-side pinned tile
  loading = false,
}) {
  if (loading) return <InsightTileSkeleton />;
  const badge = BADGE_STYLES[insight.badge] || BADGE_STYLES.Medium;
  const Icon = insight.icon;

  return (
    <Paper
      elevation={0}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        p: { xs: 1.5, sm: 2 },
        border: BORDER,
        borderRadius: 2.5,
        boxShadow: CARD_SHADOW,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {/* Top row: icon, title, badge, +/- pin toggle */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: insight.iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon sx={{ fontSize: 18, color: insight.iconColor }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13.5,
              fontWeight: 600,
              color: TEXT_HEADING,
              lineHeight: 1.3,
            }}
            noWrap
          >
            {insight.title}
          </Typography>
          <Typography
            sx={{ fontSize: 12, color: TEXT_MUTED, lineHeight: 1.4 }}
            noWrap
          >
            {insight.subtitle}
          </Typography>
        </Box>

        {/* <Chip
          label={insight.badge}
          size="small"
          sx={{
            bgcolor: badge.bg,
            color: badge.color,
            fontWeight: 700,
            fontSize: 10.5,
            height: 20,
            flexShrink: 0,
          }}
        /> */}

        {/* iconVariant controls this — "plus" here in the Insights tab
            (always, regardless of pin state); "minus" on the dashboard-side
            pinned tile component, where every listed item is already pinned. */}
        <IconButton
          size="small"
          aria-label={
            pinned ? "Remove from My Dashboard" : "Add to My Dashboard"
          }
          onClick={() => onTogglePin(insight.id)}
          sx={{ p: 0.5, flexShrink: 0 }}
        >
          {/* <Box component="img" src={iconVariant === "minus" ? minusIcon : plusIcon} sx={{ width: 14, height: 14 }} /> */}
        </IconButton>
      </Box>

      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}
      >
        {insight.type === "specialty" ? (
          <SpecialtyAuditTile insight={insight} />
        ) : insight.type === "top10drg" ? (
          <TopDrgTile insight={insight} />
        ) : insight.type === "accuracy" ? (
          <AccuracyRateTile insight={insight} />
        ) : insight.type === "codeChanges" ? (
          <CodeChangesTile insight={insight} />
        ) : insight.type === "financialImpact" ? (
          <FinancialImpactTile insight={insight} />
        ) : (
          <KPITile insight={insight} />
        )}
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #EEF2F6",
          pt: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mt: "auto",
        }}
      >
        <Typography sx={{ fontSize: 11.5, color: "#94A3B8" }} noWrap>
          Last updated: {insight.lastUpdated}
        </Typography>

        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}
        >
          {/* Always enabled in the Insights tab (disableOpenBreakdown=false).
              The dashboard-side pinned tile component passes true here,
              since a pinned item has nothing left to "open for breakdown"
              until it's removed. */}
          <Button
            size="small"
            disabled={disableOpenBreakdown}
            onClick={() => onOpenBreakdown?.(insight.id)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 12.5,
              color: BRAND_BLUE,
              bgcolor: "#EFF6FF",
              px: 1.75,
              py: 0.5,
              borderRadius: 2,
              minWidth: 0,
              "&:hover": { bgcolor: "#DBEAFE" },
              "&.Mui-disabled": { color: "#94A3B8", bgcolor: "#F1F5F9" },
            }}
          >
            Open for Breakdown
          </Button>

          <IconButton
            size="small"
            aria-label={
              pinned ? "Unpin from My Dashboard" : "Pin to My Dashboard"
            }
            onClick={() => onTogglePin(insight.id)}
            sx={{
              border: BORDER,
              borderRadius: 1.5,
              width: 32,
              height: 32,
              bgcolor: pinned ? "#EFF6FF" : "#fff",
            }}
          >
            {pinned ? (
              <PushPinIcon sx={{ fontSize: 16, color: BRAND_BLUE }} />
            ) : (
              <PushPinOutlinedIcon sx={{ fontSize: 16, color: TEXT_HEADING }} />
            )}
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Static catalog — replace with data from your slice once wired up
// ---------------------------------------------------------------------------
const INSIGHTS_CATALOG = [
  {
    id: "coder-accuracy",
    type: "accuracy",
    title: "Coder Accuracy Rate",
    subtitle: "Shows coding accuracy across reviewed episodes.",
    apiTileName: "Coder Accuracy Rate",
    badge: "Good",
    icon: SpeedIcon,
    iconBg: "#EFF6FF",
    iconColor: BRAND_BLUE,
    value: "94.8%",
    correct: 519,
    incorrect: 29,
    reviewedEpisodes: 548,
    lastUpdated: "Today, 09:30 AM",
  },
  {
    id: "diagnosis-changes",
    type: "codeChanges",
    title: "Code Changes",
    subtitle: "Monitor all code changes identified during audits.",
    badge: "Medium",
    apiTileName: "Code Changes",
    icon: ChangeCircleOutlinedIcon,
    iconBg: "#ECFDF5",
    iconColor: "#16A34A",
    value: "2,322",
    categories: [
      {
        label: "Principal Diagnosis",
        value: 1246,
        percent: 53.7,
        color: BRAND_BLUE,
      },
      {
        label: "Additional Diagnosis",
        value: 684,
        percent: 29.5,
        color: "#8B5CF6",
      },
      {
        label: "Procedure Code Changes",
        value: 392,
        percent: 16.9,
        color: "#F59E0B",
      },
    ],
    lastUpdated: "Today, 09:30 AM",
  },
  {
    id: "top-drg-changes",
    type: "top10drg",
    title: "Top 10 DRG Changes",
    subtitle: "DRGs with the highest changes in coding.",
    badge: "Medium",
    apiTileName: "Top 10 DRG",
    icon: LeaderboardOutlinedIcon,
    iconBg: "#EFF6FF",
    iconColor: BRAND_BLUE,
    drgs: [
      { rank: 1, name: "MS-DRG 470", count: 212 },
      { rank: 2, name: "MS-DRG 469", count: 168 },
      { rank: 3, name: "MS-DRG 371", count: 132 },
      { rank: 4, name: "MS-DRG 291", count: 98 },
      { rank: 5, name: "MS-DRG 194", count: 74 },
    ],
    lastUpdated: "Today, 09:30 AM",
  },
  {
    id: "specialty-audit-outcomes",
    type: "specialty",
    title: "Speciality Audit Outcomes",
    subtitle: "Audit completion and outcomes by most audited specialties.",
    badge: "Good",
    apiTileName: "Speciality Audit Outcomes",
    icon: DonutSmallOutlinedIcon,
    iconBg: "#EFF6FF",
    iconColor: "#16A34A",
    completionRate: 78.6,
    topSpecialty: { name: "Cardiology", episodes: 1245 },
    statusBreakdown: { noChange: 100, recoded: 25, cdqSent: 75 },
    lastUpdated: "Today, 09:30 AM",
  },
  {
    id: "confirmed-financial-impact",
    type: "financialImpact",
    title: "Confirmed Financial Impact",
    subtitle: "Total financial impact from coding improvements.",
    badge: "Good",
    apiTileName: "Confirmed Financial Impact",
    icon: PaidOutlinedIcon,
    iconBg: "#ECFDF5",
    iconColor: "#16A34A",
    value: "$1.82M",
    totalConfirmedRevenue: 2082500,
    avgRevenuePerEpisode: 430000,
    lastUpdated: "Today, 09:30 AM",
  },
];

export const TILE_ID_TO_NAME = Object.fromEntries(
  INSIGHTS_CATALOG.map((i) => [i.id, i.apiTileName]),
);
export const TILE_NAME_TO_ID = Object.fromEntries(
  INSIGHTS_CATALOG.map((i) => [i.apiTileName, i.id]),
);
// Add near the top of InsightsView.jsx, above INSIGHTS_CATALOG
const parsePercent = (val) => {
  if (typeof val === "number") return val;
  return parseFloat(String(val).replace("%", "")) || 0;
};

function buildLiveInsight(insight, liveData) {
  if (insight.id === "coder-accuracy" && liveData.coderAccuracy) {
    return {
      ...insight,
      value: liveData.coderAccuracy.accuracy_rate,
      correct: liveData.coderAccuracy.review_outcomes?.correct ?? 0,
      incorrect: liveData.coderAccuracy.review_outcomes?.incorrect ?? 0,
      reviewedEpisodes: liveData.coderAccuracy.reviewed_episodes ?? 0,
      lastUpdated: formatRelativeTime(liveData.coderAccuracyLastUpdated),
    };
  }

  if (insight.id === "diagnosis-changes" && liveData.codeChanges) {
    const categoryColors = [BRAND_BLUE, "#8B5CF6", "#F59E0B"];
    return {
      ...insight,
      value:
        liveData.codeChanges.total_code_changes?.toLocaleString?.() ??
        String(liveData.codeChanges.total_code_changes ?? ""),
      categories: (liveData.codeChanges.data || []).map((c, i) => ({
        label: c.change_category,
        value: Number(c.no_of_changes) || 0,
        percent: parsePercent(c.percentage),
        color: categoryColors[i % categoryColors.length],
      })),
      lastUpdated: formatRelativeTime(liveData.codeChangesLastUpdated),
    };
  }

  if (insight.id === "top-drg-changes" && liveData.topDrg) {
    const drgs = (liveData.topDrg.top10_most_common_drgs || []).map((d, i) => ({
      rank: i + 1,
      name: `MS-DRG ${d.drg_code}`,
      count: d.value,
    }));
    return {
      ...insight,
      drgs,
      lastUpdated: formatRelativeTime(liveData.topDrgLastUpdated),
    };
  }
  // InsightsView.jsx — buildLiveInsight()
  if (
    insight.id === "specialty-audit-outcomes" &&
    liveData.specialtyAuditOutcomes
  ) {
    const d = liveData.specialtyAuditOutcomes;
    const breakdown = d.top_speciality_status_breakdown || {};

    return {
      ...insight,
      // No true "completion rate" field exists on this endpoint —
      // using the top specialty's share of total audited episodes instead.
      completionRate: parsePercent(d.percentage_of_total),
      topSpecialty: {
        name: d.top_audited_speciality,
        episodes: d.top_speciality_count,
      },
      statusBreakdown: {
        noChange: breakdown.no_change ?? 0,
        recoded: breakdown.recoded ?? 0,
        cdqSent: breakdown.cdq_sent ?? 0,
      },
        lastUpdated: formatRelativeTime(liveData.specialtyAuditOutcomesLastUpdated),
    };
  }
  if (insight.id === "confirmed-financial-impact" && liveData.financialImpact) {
    const d = liveData.financialImpact;
    return {
      ...insight,
      value: `$${d.total_confirmed_revenue}`, // already formatted, e.g. "$1.02M"
      totalConfirmedRevenue: d.total_confirmed_revenue, // string as-is — see note below
      avgRevenuePerEpisode: d.avg_revenue_per_episode, // real number, toLocaleString works correctly
      lastUpdated: formatRelativeTime(liveData.financialImpactLastUpdated),
      totalConfirmedEpisodes: d.audited_episodes, // 
    };
  }

  return insight;
}

// Replace the exported lookup with this — same helper, just fed a single id
export function INSIGHTS_LOOKUP(id, liveData = {}) {
  const base = INSIGHTS_CATALOG.find((i) => i.id === id);
  if (!base) return null;
  return buildLiveInsight(base, liveData);
}

// ---------------------------------------------------------------------------
// InsightsView — main tab content
// ---------------------------------------------------------------------------
export default function InsightsView({ pinnedIds, onTogglePin }) {
  const [openBreakdownId, setOpenBreakdownId] = useState(null);
  const { filters, coderAccuracy, coderAccuracyLastUpdated, codeChanges, codeChangesLastUpdated,
  topDrg, topDrgLastUpdated, specialtyAuditOutcomes, specialtyAuditOutcomesLastUpdated,
  financialImpact, financialImpactLastUpdated,    coderAccuracyLoading,
    codeChangesLoading,
    topDrgLoading,
    specialtyAuditOutcomesLoading,
    financialImpactLoading, } = useSelector((state) => state.workflowDashboard);
  const dispatch = useDispatch();
  useRelativeTimeTick(30000);


useEffect(() => {
  fetchInsightsByIds(dispatch, filters, Object.keys(INSIGHT_FETCH_MAP));
}, [filters]);

  // Single source of truth — reuse the same helper INSIGHTS_LOOKUP uses,
  // instead of a second hand-rolled map that can drift out of sync.
 const liveData = {
  coderAccuracy, coderAccuracyLastUpdated,
  codeChanges, codeChangesLastUpdated,
  topDrg, topDrgLastUpdated,
  specialtyAuditOutcomes, specialtyAuditOutcomesLastUpdated,
  financialImpact, financialImpactLastUpdated,
};
  const catalog = INSIGHTS_CATALOG.map((insight) =>
    buildLiveInsight(insight, liveData),
  );
  const LOADING_BY_ID = {
  "coder-accuracy": coderAccuracyLoading,
  "diagnosis-changes": codeChangesLoading,
  "top-drg-changes": topDrgLoading,
  "specialty-audit-outcomes": specialtyAuditOutcomesLoading,
  "confirmed-financial-impact": financialImpactLoading,
};
  const handleOpenBreakdown = (id) => setOpenBreakdownId(id);
  const handleCloseBreakdown = () => setOpenBreakdownId(null);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        px: { xs: 1.5, sm: 0 },
      }}
    >
      {/* <Typography
        sx={{
          fontSize: { xs: 16, sm: 18 },
          fontWeight: 700,
          color: TEXT_HEADING,
          mb: 0.5,
        }}
      >
        Insights
      </Typography>
      <Typography
        sx={{ fontSize: { xs: 12.5, sm: 13 }, color: TEXT_MUTED, mb: 2 }}
      >
        Explore coding, audit, DRG and financial summary statistics. Select any
        summary tile to investigate detailed analytics.
      </Typography> */}

      {/* Top banner — stacks icon above text on phones */}
      {/* <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          gap: 1,
          bgcolor: "#EFF6FF",
          border: "1px solid #DBEAFE",
          borderRadius: 1.5,
          px: { xs: 1.5, sm: 2 },
          py: 1,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <InfoOutlinedIcon
            sx={{ fontSize: 18, color: BRAND_BLUE, flexShrink: 0 }}
          />
          <Typography sx={{ fontSize: 12.5, color: "#334155" }}>
            Pin any summary tile to make it available in My Dashboard after
            viewing its breakdown.
          </Typography>
        </Box>
      </Box> */}

      {/* Responsive grid: 1 column on phones, 2 on tablets, 3 on desktop.
          Replaces the old fixed row-chunking so tiles reflow automatically. */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)",
          },
          gap: { xs: 2, sm: 2.5 },
          flex: 1,
          mb: 2.5,
        }}
      >
        {catalog.map((insight) => (
          <InsightTile
            key={insight.id}
            insight={insight}
            pinned={pinnedIds.includes(insight.id)}
            onTogglePin={onTogglePin}
            onOpenBreakdown={handleOpenBreakdown}
            loading={LOADING_BY_ID[insight.id] ?? false}
          />
        ))}
      </Box>

      {/* Bottom banner — same stacking treatment as the top one */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 1,
          bgcolor: "#EFF6FF",
          border: "1px solid #DBEAFE",
          borderRadius: 1.5,
          px: { xs: 1.5, sm: 2 },
          py: 1,
        }}
      >
        <InfoOutlinedIcon
          sx={{ fontSize: 18, color: BRAND_BLUE, flexShrink: 0 }}
        />
        <Typography sx={{ fontSize: 12.5, color: "#334155" }}>
          Selecting a summary tile opens a detailed Breakdown view in an
          overlay. In the Breakdown view, you can analyse data, change chart
          types, download graphics and add the insight to My Dashboard.
        </Typography>
      </Box>

      {/* One router renders whichever breakdown modal is currently open —
          every tile shares the same modal shell from BreakdownModal.jsx */}
      <BreakdownRouter
        openId={openBreakdownId}
        onClose={handleCloseBreakdown}
        pinnedIds={pinnedIds}
        onTogglePin={onTogglePin}
      />
    </Box>
  );
}

export { InsightTile };
